import React from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import { alpha, useTheme } from '@mui/material/styles'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { partResultLabel } from '../../../../generic/utilities/partResultLabel'
import { partLifecycleGroupLabel, PART_LIFECYCLE_GROUPS } from '../partLifecycleGroup'
import { raisedColor } from '../detailSurfaces'
import { CONTROL_RADIUS } from '../detailControls'

// RN66: the non-cancelled groups render together, in the enum's reading order; CANCELLED always
// gets its OWN section below (S224) and is never mixed into these — it is only a client-side
// partition of the SINGLE already-fetched page (never a second fetch, P-7).
// ⚠️ SE DERIVA DEL ENUM, no se reescribe: era una lista literal de tres, y una lista literal es
// justo lo que se queda sin el grupo nuevo el día que Sun publica el quinto (S416).
export const GROUP_ORDER = PART_LIFECYCLE_GROUPS.filter((group) => group !== 'CANCELLED')

// Pasada 18 (§6.2, mockup 06-piezas.png): one colour per lifecycle group, so the chip grid reads
// at a glance — the group name is still printed on hover/selection (title) and the group/result
// texts stay in the DOM regardless of colour (RN66 invariant untouched).
// §2.1 — «sin empezar» es el estado INACTIVA de la tabla de estados: gris apagado, sin color
// semántico. Comparte tono con las canceladas y eso está bien mientras el color no sea lo único
// que las separa (RN35): cada chip lleva su grupo en el `title` y en el texto de la fila, y las
// canceladas viven además en su propia sección con encabezado.
const GROUP_COLOR_KEY = { NOT_STARTED: 'off', FINISHED: 'success', IN_PROGRESS: 'info', REJECTED: 'error', CANCELLED: 'text' }

// `off` y `text` no son paletas de MUI (no tienen `.main`), así que se resuelven aparte.
export const groupColor = (theme, group) => {
  const colorKey = GROUP_COLOR_KEY[group] || 'text'
  if (colorKey === 'off') return theme.palette.text.disabled
  if (colorKey === 'text') return theme.palette.text.secondary
  return theme.palette[colorKey].main
}

// Pasada 18: the chip's visible face is just code + colour (§6.2's "icono + número"); group and
// result stay as real (visually clipped, never `display:none`) text inside the SAME row element
// the tests already query by `part-row-<id>` — RN66's invariant (group and result are two
// distinct i18n keys, neither ever substituting the other) is unchanged, only the layout is new.
export const PartChip = ({ part, selected, onSelect }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const color = groupColor(theme, part.lifecycleGroup)
  const isSelected = selected
  return (
    <Box
      data-testid={`part-row-${part.partId}`}
    >
      {/* The Chip itself is the button: it takes the focus and answers Enter and Space. */}
      <Chip
        label={part.code}
        onClick={() => onSelect(part.partId)}
        title={`${partLifecycleGroupLabel(part.lifecycleGroup, t_)} · ${partResultLabel(part.result, t_)}`}
        sx={{
          fontWeight: 700,
          color,
          backgroundColor: alpha(color, 0.12),
          border: isSelected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${alpha(color, 0.3)}`
        }}
      />
      {/* Verificación del hub: este envoltorio medía 1320x828 y desbordaba el diálogo 389px.
          En el `sx` de MUI un valor numérico entre 0 y 1 es una FRACCIÓN, no píxeles: `width: 1`
          significaba `100%`, y `clip` oculta el pintado pero no encoge la caja. Unidades
          explícitas + el patrón visually-hidden completo. Los separadores evitan además que el
          texto se lea pegado ("HUN-00003In progress") en cualquier extracción de texto. */}
      <Box
        component='span'
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          border: 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)'
        }}
      >
        <span data-testid='part-code'>{part.code}</span>
        {' · '}
        <span data-testid='part-group'>{partLifecycleGroupLabel(part.lifecycleGroup, t_)}</span>
        {!!partResultLabel(part.result, t_) && ' · '}
        <span data-testid='part-result'>{partResultLabel(part.result, t_)}</span>
      </Box>
    </Box>
  )
}

// The Instances card: the chips of the loaded page, grouped in the enum's reading order, with its pager.
const PartsGrid = ({ parts, selectedPartId, onSelectPart, shownParts, hasMoreParts, loadingMore, loadMoreFailed, onLoadMore }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const rowsByGroup = {}
  ;(parts.content || []).forEach((part) => {
    const key = part.lifecycleGroup
    rowsByGroup[key] = rowsByGroup[key] || []
    rowsByGroup[key].push(part)
  })

  return (
    <Box
      data-testid='parts-instances-card'
      sx={{
        // S460 — «Selected part» ya no pide un tercio fijo (`flex: 1`): el hueco que libera
        // lo absorbe esta tarjeta, que sigue siendo la que crece.
        flex: 1,
        minWidth: 0,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
        backgroundColor: raisedColor(theme),
        p: 2.5
      }}
    >
      <Typography variant='caption' sx={{ fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'text.secondary', display: 'block', mb: 1.5 }}>
        {t_('Instances')}
      </Typography>
      <Box data-testid='parts-grid' sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {GROUP_ORDER.flatMap((group) => rowsByGroup[group] || []).map((part) => (
          <PartChip key={part.partId} part={part} selected={part.partId === selectedPartId} onSelect={onSelectPart} />
        ))}
      </Box>
      {/* The pager sits INSIDE the Instances card so it is seen with the grid it pages. It
      paginates only the instance grid (excludeCancelled): cancelled parts have their
      own request, count and «Show more» in their section. */}
      {parts.totalElements > 0 && (
        <Stack data-testid='parts-page-footer' direction='row' spacing={2} alignItems='center' sx={{ mt: 1.5 }}>
          <Typography data-testid='parts-shown-count' variant='body2' sx={{ color: 'text.secondary' }}>
            {t_('Showing {{shown}} of {{total}}', { shown: shownParts, total: parts.totalElements })}
          </Typography>
          {hasMoreParts && (
            <Button
              data-testid='parts-load-more'
              size='small'
              variant='outlined'
              onClick={onLoadMore}
              disabled={loadingMore}
              sx={{ textTransform: 'none', borderRadius: CONTROL_RADIUS, minHeight: 44 }}
            >
              {loadingMore ? t_('Loading...') : t_('Show more parts')}
            </Button>
          )}
          {loadMoreFailed && (
            <Typography data-testid='parts-load-more-error' variant='body2' sx={{ color: 'error.main' }}>{t_('Could not load the parts')}</Typography>
          )}
        </Stack>
      )}
    </Box>
  )
}

export default PartsGrid
